import { create } from 'zustand';
import type { DmChannel } from '../types';
import * as api from '../lib/api';
import { useChannelStore } from './useChannelStore';

interface DmState {
  dmChannels: DmChannel[];

  setDmChannels: (channels: DmChannel[]) => void;
  addDmChannel: (channel: DmChannel) => void;
  updateDmChannel: (channelId: string, updates: Partial<DmChannel>) => void;
  clearUnread: (channelId: string) => void;
  removeDmChannel: (channelId: string) => void;
  reset: () => void;

  loadDmChannels: () => Promise<void>;
  openDm: (userId: string) => Promise<DmChannel>;
}

export const useDmStore = create<DmState>()((set, get) => ({
  dmChannels: [],

  setDmChannels: (channels) => set({ dmChannels: channels }),

  addDmChannel: (channel) => set((s) => {
    if (s.dmChannels.some(dm => dm.id === channel.id)) return s;
    return { dmChannels: [...s.dmChannels, channel] };
  }),

  updateDmChannel: (channelId, updates) => set((s) => ({
    dmChannels: s.dmChannels.map(dm => dm.id === channelId ? { ...dm, ...updates } : dm),
  })),

  clearUnread: (channelId) => set((s) => ({
    dmChannels: s.dmChannels.map(dm => dm.id === channelId ? { ...dm, unread_count: 0 } : dm),
  })),

  removeDmChannel: (channelId) => set((s) => ({
    dmChannels: s.dmChannels.filter(dm => dm.id !== channelId),
  })),

  reset: () => set({ dmChannels: [] }),

  loadDmChannels: async () => {
    try {
      const channels = await api.fetchDmChannels();
      set({ dmChannels: channels });
    } catch {
      // Ignore
    }
  },

  openDm: async (userId) => {
    const dm = await api.createOrGetDm(userId);
    get().addDmChannel(dm);
    const channelStore = useChannelStore.getState();
    channelStore.setCurrentChannel(dm.id);
    channelStore.clearUnread(dm.id);
    get().clearUnread(dm.id);
    api.markChannelRead(dm.id).catch(() => {});
    return dm;
  },
}));
